import { useEffect, useState } from 'react';
import { MessageTestingSocketEvent } from '@trinity/chat';
import type {
  MessageTestingCell,
  MessageTestingProgress,
  MessageTestingResult,
  MessageTestingRollups,
} from '@trinity/chat/types/message-testing';
import { useMessageTestingSocket } from '@trinity/chat-components/websocket';
import type { RunStatus } from '../components/MessageTestingGrid/types';

export interface MessageTestingRunState {
  status: RunStatus;
  progress: { completed: number; total: number };
  cells: MessageTestingCell[];
  cellErrors: Map<string, string>;
  error: string | null;
  rollups: MessageTestingRollups | null;
}

export function useMessageTestingRun(runId: number): MessageTestingRunState {
  const socket = useMessageTestingSocket(runId);
  const [status, setStatus] = useState<RunStatus>('connecting');
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [cells, setCells] = useState<MessageTestingCell[]>([]);
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [rollups, setRollups] = useState<MessageTestingRollups | null>(null);

  useEffect(() => {
    if (!socket) return;

    setStatus('processing');

    const handleProgress = (payload: MessageTestingProgress): void => {
      setProgress({ completed: payload.completed, total: payload.total });
      if (payload.latestCell) {
        setCells((prev) => [...prev, payload.latestCell]);
      }
    };

    const handleCellError = (payload: { messageId: string; segmentId: string; error: string }): void => {
      setCellErrors((prev) => {
        const next = new Map(prev);
        next.set(`${payload.messageId}:${payload.segmentId}`, payload.error);
        return next;
      });
    };

    const handleCellsCompleted = (payload: Pick<MessageTestingResult, 'runId' | 'matrix'>): void => {
      setCells(payload.matrix);
      setProgress({ completed: payload.matrix.length, total: payload.matrix.length });
      setStatus('analyzing');
    };

    const handleCompleted = (
      payload: Pick<MessageTestingResult, 'runId' | 'matrix'> & { rollups?: MessageTestingRollups }
    ): void => {
      setCells(payload.matrix);
      setProgress({ completed: payload.matrix.length, total: payload.matrix.length });
      if (payload.rollups) setRollups(payload.rollups);
      setStatus('completed');
    };

    const handleFailed = (payload: { runId: number; error: string }): void => {
      setError(payload.error);
      setStatus('failed');
    };

    socket.on(MessageTestingSocketEvent.Progress, handleProgress);
    socket.on(MessageTestingSocketEvent.CellError, handleCellError);
    socket.on(MessageTestingSocketEvent.CellsCompleted, handleCellsCompleted);
    socket.on(MessageTestingSocketEvent.Completed, handleCompleted);
    socket.on(MessageTestingSocketEvent.Failed, handleFailed);

    return () => {
      socket.off(MessageTestingSocketEvent.Progress, handleProgress);
      socket.off(MessageTestingSocketEvent.CellError, handleCellError);
      socket.off(MessageTestingSocketEvent.CellsCompleted, handleCellsCompleted);
      socket.off(MessageTestingSocketEvent.Completed, handleCompleted);
      socket.off(MessageTestingSocketEvent.Failed, handleFailed);
    };
  }, [socket]);

  return { status, progress, cells, cellErrors, error, rollups };
}
